/**
 * Read a screen's "Mode / capability" contract cell into something renderable.
 *
 * Story #5343 (Launch W1). The workbook authors this column as prose, so there
 * are 98 distinct strings across 143 screens -- "Read-only triage and
 * drill-down (R0)", "governed_customer_record_writer", "R0 inventory; R2
 * lifecycle/password; R3 privileged access or authentication recovery". They
 * share one taxonomy underneath: the R0-R3 risk tiers.
 *
 *   R0  read only -- nothing on this screen changes anything
 *   R1  personal or draft change -- a saved view, a preference, an unsubmitted draft
 *   R2  governed change -- a real change, subject to policy
 *   R3  privileged change -- step-up, approval, or destructive
 *
 * HONESTY RULE: when a mode string cannot be parsed, this returns tier `null`
 * and the caller shows the raw contract text with no badge. Guessing a tier
 * would be worse than showing nothing -- a screen wrongly labelled "Read-only"
 * invites an operator to click something consequential.
 */

export type RiskTier = 'R0' | 'R1' | 'R2' | 'R3';

export interface Capability {
  /** Every tier the contract names, ascending. Empty when unparseable. */
  tiers: RiskTier[];
  /** The riskiest thing this screen can do, or null when unparseable. */
  highest: RiskTier | null;
  /** True only when R0 is the whole story. */
  readOnly: boolean;
  /** Short badge text, e.g. 'Read-only'. Null when unparseable. */
  label: string | null;
  /** One plain sentence for the footer callout. Null when unparseable. */
  summary: string | null;
  /** The contract cell verbatim, always present. */
  raw: string;
}

const TIER_LABEL: Record<RiskTier, string> = {
  R0: 'Read-only',
  R1: 'Personal or draft change',
  R2: 'Governed change',
  R3: 'Privileged change',
};

const TIER_SUMMARY: Record<RiskTier, string> = {
  R0: 'This screen only reads. Nothing here changes anything.',
  R1: 'This screen can save personal settings or an unsubmitted draft. Nothing is applied to the platform.',
  R2: 'This screen can make a real change, subject to policy checks.',
  R3: 'This screen can make a privileged change. Expect step-up authentication, approval, or an irreversible effect.',
};

/**
 * snake_case capability tokens the workbook uses instead of a tier. Mapped by
 * what the token actually permits, not by its name -- `governed_*` always
 * means a real write, `read_*` never does.
 */
const TOKEN_TIERS: ReadonlyArray<[RegExp, RiskTier]> = [
  [/^read_only_\w+$/, 'R0'],
  [/^read_projection$/, 'R0'],
  [/^independent_verification$/, 'R0'],
  [/^governed_\w+$/, 'R2'],
];

/**
 * Prose forms carrying no explicit tier. Ordered: the first match wins, so put
 * the more specific phrasings first.
 */
const PROSE_TIERS: ReadonlyArray<[RegExp, RiskTier]> = [
  [/versioned administrative change/i, 'R2'],
  [/governed operation/i, 'R2'],
  [/draft scoped|draft hand-off/i, 'R1'],
  [/^viewing\b|^read\b|^joined read|read-only|no command target/i, 'R0'],
];

/**
 * A clause that hands its tier to a DIFFERENT screen, not to this one.
 *
 * The workbook routinely names, in one cell, both what a screen does and what
 * it hands off. E11 reads "Read-only access explanation (R0); all changes hand
 * off to Identity R2/R3": taking the maximum badged a read-only projection
 * "Privileged change" in red. E01 reads "...(R0); Plan environment is a
 * separate R1 flow" and got "Personal or draft change" on a fleet list. Both
 * are lies about what the screen in front of the operator can do.
 *
 * All 31 multi-tier rows were read by hand on 2026-09-03; exactly three
 * phrasings hand a tier away, and they cover ten rows:
 *
 *   "hand off" / "hand-off" / "hand-offs"   E03 E07 E11 E13 E14 E15 E17 M02 G06
 *   "separate ... flow"                     E01
 *   "opens governed <domain> job"           E15
 *
 * The other 21 (H07 "View R0; ... personal filter R1", E12, A02, M03, G05 ...)
 * really do both things here, and keep the maximum. The handed-off tiers are
 * never hidden: the badge's tooltip and the disclosure both carry the cell
 * verbatim.
 */
const BELONGS_ELSEWHERE =
  /hand[-\s]?offs?\b|\bseparate\b|\bopens\b[^;.]*\bjob\b|\belsewhere\b|\boccurs? (?:in|only in)\b|\bremains? (?:in|with)\b|\bhandled (?:in|by)\b/i;

/** Parse a "Mode / capability" cell. Never throws; never invents a tier. */
export function parseCapability(raw: string | undefined | null): Capability {
  const text = (raw ?? '').trim();
  const unparsed: Capability = {
    tiers: [], highest: null, readOnly: false, label: null, summary: null, raw: text,
  };
  if (!text) return unparsed;

  const found = new Set<RiskTier>();

  // 1. Explicit R0-R3 tokens, clause by clause. `R0/R1` and `R2/R3` both
  //    appear, so scan globally rather than taking the first match -- but skip
  //    a clause that attributes its tier to another surface.
  const clauses = text.split(/;|\.\s/);
  for (const clause of clauses) {
    if (BELONGS_ELSEWHERE.test(clause)) continue;
    for (const match of clause.matchAll(/\bR([0-3])\b/g)) {
      found.add(`R${match[1]}` as RiskTier);
    }
  }
  // Every clause disclaimed? Then the disclaimers are the whole cell, and the
  // tiers in them are the best evidence there is. Better a tier from odd
  // phrasing than no badge at all on a screen the contract clearly tiers.
  if (found.size === 0) {
    for (const match of text.matchAll(/\bR([0-3])\b/g)) {
      found.add(`R${match[1]}` as RiskTier);
    }
  }

  // 2. snake_case capability tokens.
  if (found.size === 0) {
    for (const [pattern, tier] of TOKEN_TIERS) {
      if (pattern.test(text)) { found.add(tier); break; }
    }
  }

  // 3. Prose fallback.
  if (found.size === 0) {
    for (const [pattern, tier] of PROSE_TIERS) {
      if (pattern.test(text)) { found.add(tier); break; }
    }
  }

  if (found.size === 0) return unparsed;

  const tiers = [...found].sort();
  const highest = tiers[tiers.length - 1];
  return {
    tiers,
    highest,
    readOnly: highest === 'R0',
    label: TIER_LABEL[highest],
    summary: TIER_SUMMARY[highest],
    raw: text,
  };
}

/** Tailwind classes for a tier badge, in the app's sky palette. */
export function tierBadgeClasses(tier: RiskTier | null): string {
  switch (tier) {
    case 'R0': return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'R1': return 'bg-sky-50 text-sky-800 border-sky-200';
    case 'R2': return 'bg-amber-50 text-amber-900 border-amber-300';
    case 'R3': return 'bg-red-50 text-red-800 border-red-300';
    default: return 'bg-slate-50 text-slate-500 border-slate-200';
  }
}
